import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import useSearchUsers from '../../Hooks/useSearchUsers';
import AutoCompletableInput from '../AutoCompletableInputComponent/AutoCompletableInput';

const UserLookupInput = ({
    value,
    handleValueChange,
    noValidate = true,
    displayProperty = "userName",
    isValueValid,
    handleUserRecordSelection,
    inputLabel,
    placeholder = "",
    customErrorMessage = "",
    disabled = false,
    isRequired = false
}) => {
    const [hasError, setHasError] = useState(false);

    const {
        userSearchResults,
        loadingUsers,
        lookupUsers
    } = useSearchUsers();

    // Transform search results to options format
    const options = useMemo(() => {
        if (!userSearchResults || !Array.isArray(userSearchResults)) {
            return [];
        }
        
        let options = userSearchResults.map(result => ({
            value: result.userName,
            label: result.userName,
            data: result
        }));
        
        // If we have a value that's not in the search results, add it as an option
        if (value && !options.find(option => option.value === value)) {
            options.push({
                value: value,
                label: value,
                data: { userName: value, userId: '' } // Synthetic data for external values
            });
        }
        
        return options;
    }, [userSearchResults, value]);

    // Handle input value changes
    const handleChange = useCallback((inputValue) => {
        handleValueChange(inputValue);
    }, [handleValueChange]);

    // Handle search triggering
    const handleSearch = useCallback(async (inputValue) => {
        if (inputValue && inputValue.length >= 2) {
            try {
                await lookupUsers(inputValue);
            } catch (error) {
                console.error(error);
                setHasError(true);
            }
        }
    }, [lookupUsers]);

    const handleSelect = useCallback((selectedData) => {
        // Only call handleUserRecordSelection if we have real user data (not synthetic)
        if (selectedData.data.userId) {
            handleUserRecordSelection(selectedData.data);
        }
        handleValueChange(selectedData.data.userName);
    }, [handleUserRecordSelection, handleValueChange]);

    return (
        <div className='user-lookup-input'>
            <AutoCompletableInput
                value={value}
                onChange={handleChange}
                options={options}
                onInputChange={handleSearch}
                isLoading={loadingUsers}
                placeholder={placeholder}
                label={inputLabel}
                isInvalid={!noValidate && !isValueValid}
                errorMsg={customErrorMessage}
                disabled={disabled}
                displayKey="label"
                valueKey="value"
                onSelect={handleSelect}
                minimumInputLength={2}
            />

            {hasError && <Alert variant="danger">Error fetching users</Alert>}
        </div>
    );
};

UserLookupInput.propTypes = {
    handleValueChange: PropTypes.func.isRequired,
    handleUserRecordSelection: PropTypes.func.isRequired,
    displayProperty: PropTypes.string,
    value: PropTypes.string,
    noValidate: PropTypes.bool,
    isValueValid: PropTypes.bool,
    inputLabel: PropTypes.string,
    placeholder: PropTypes.string,
    customErrorMessage: PropTypes.string,
    disabled: PropTypes.bool,
    isRequired: PropTypes.bool
};

export default UserLookupInput;